import { isEmpty } from 'lodash';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import { getEmptyRequiredFields, isValidDBColumnName } from 'js/lib/ValidationUtils';
import UIUtils from 'js/lib/UIUtils';
import createFlowService from 'new/components/flows/service/createFlowService';
import { CONNECTORS_WITHOUT_PATH } from 'js/Constants';

export default function createDatabaseService() {
  function buildSourceConfig({ sourceConfig, configFields }) {
    if (sourceConfig.db_query_mode === 'Query') {
      delete sourceConfig['incrementing.column.name'];
      delete sourceConfig['incrementing.load.from'];
      delete sourceConfig['timestamp.column.name'];
      delete sourceConfig['timestamp.load.from'];
    }

    return UIUtils.reduceConfig(sourceConfig, configFields);
  }

  function getResourceName(selectedTable, selectedCredential) {
    return (connectionCategory) => {
      if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
        let sinkName = '';
        const database = selectedTable?.database;
        const name = selectedTable?.name;

        const id = selectedTable && selectedTable?.id ? selectedTable.id : null;

        if (database && name) {
          sinkName = `${database} - ${name}`;
        } else if (id) {
          sinkName = `${id}`;
        } else if (database) {
          sinkName = `${database}`;
        } else {
          sinkName = `${selectedCredential && selectedCredential.name} - Query`;
        }
        return sinkName;
      }
    };
  }

  function validateSink(
    { configFields, config, selectedConnection, selectedBucket, isNewTable, selectedTable, tables },
    onError,
  ) {
    const { defaultSinkValidate } = createFlowService();

    if (!CONNECTORS_WITHOUT_PATH.includes(selectedConnection) && !selectedBucket) {
      if (isEmpty(selectedBucket)) {
        onError('Please select a table');
        return false;
      } else {
        const { database } = selectedBucket;
        if (!database) {
          onError('Please select a table');
          return false;
        }
      }
    }

    const mapping = config?.mapping?.mapping || {};

    if (
      (selectedConnection && config?.mapping?.mode !== 'auto' && isEmpty(mapping)) ||
      !!mapping.fields_order
    ) {
      onError('Please choose at least one attribute.');
      return false;
    }

    if (isNewTable) {
      const { database, table } = config;

      if (isEmpty(table)) {
        onError('Please assign a name to the table.');
        return;
      }

      if (database && tables[database] && tables[database]?.indexOf?.(table) > -1) {
        onError(`A table named "${table}" already exists.`);
        return;
      }

      if (!isValidDBColumnName(table, selectedConnection)) {
        onError(`Invalid table name: ${table}`);
        return;
      }

      const columns = selectedTable?.columns || [];

      for (let i = columns.length - 1; i > -1; i--) {
        const columnName = columns[i].name;

        if (!isValidDBColumnName(columnName, selectedConnection)) {
          onError(`Invalid column name: ${columnName}`);
          return;
        }
      }
    }

    if (!defaultSinkValidate(config, configFields, onError)) {
      return false;
    }

    return true;
  }

  function validateSource({ selectedBucket, configFields, config, selectedConnection }, onError) {
    const { defaultSourceValidate } = createFlowService();
    if (
      !defaultSourceValidate(
        {
          sourceConfig: config,
          sourceConfigFields: configFields,
          selectedConnection,
          selectedBucket,
          connectionCategory: CONNECTION_CATEGORY_DATABASE,
        },
        onError,
      )
    ) {
      return false;
    }

    if (!CONNECTORS_WITHOUT_PATH.includes(selectedConnection) && !selectedBucket) {
      if (!config.db_query_mode || config.db_query_mode === 'Default') {
        onError('Please select a table to scan.');
        return;
      }
    }

    return true;
  }

  function validateDatabase(
    {
      selectedBucket,
      configFields,
      config,
      selectedConnection,
      isNewTable,
      selectedTable,
      tables,
      connectionType,
    },
    onError,
  ) {
    if (connectionType === 'SOURCE') {
      return validateSource(
        {
          selectedBucket,
          configFields,
          config,
          selectedConnection,
        },
        onError,
      );
    } else {
      return validateSink(
        {
          configFields,
          config,
          selectedConnection,
          selectedBucket,
          isNewTable,
          selectedTable,
          tables,
        },
        onError,
      );
    }
  }

  return {
    buildSourceConfig,
    getResourceName,
    validateDatabase,
  };
}
